import React, { useMemo } from 'react';
import useGameStore from '../store/gameStore';
import { useTranslation } from '../i18n';
import { INVESTMENTS } from '../data/investments';
import { VEHICLES } from '../data/vehicles';
import { estimateContractMargin } from '../utils/contractMargin';
import { formatMoney } from '../utils/format';
import {
  LineChart as LineChartIcon, TrendingUp, TrendingDown, Users, Truck, Target,
  ShieldCheck, Briefcase, Scale, Award, Star, AlertTriangle, Gauge, Sparkles,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
// 08/02/2026 — `SupportBenefitsPanel` retiré (déplacé vers /fonctions-support).
// import SupportBenefitsPanel from '../components/strategie/SupportBenefitsPanel';
import ContractLedgerPanel from '../components/strategie/ContractLedgerPanel';
import CommercialPerformancePanel from '../components/strategy/CommercialPerformancePanel';
import { InteractiveMap } from '../components/game/InteractiveMap';
import { computeBalanceSheet } from '../utils/balanceSheet';
import BilanFiscalBreakdown from '../components/fiscalite/BilanFiscalBreakdown';
import MarkdownLite from '../components/MarkdownLite';

// Recharts inline-prop constants hoisted to avoid object re-creation on
// every render (this page re-renders on each game state change).
const CHART_MARGIN = { top: 5, right: 10, bottom: 5, left: -20 };
const CHART_GRID_STROKE = 'rgba(148,163,184,0.15)';
const TOOLTIP_STYLE = { backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: 6, fontSize: 11 };
const LEGEND_STYLE = { fontSize: 11 };
const Y_DOMAIN = [0, 100];

/**
 * CEO Dashboard — "Tableau de bord stratégique".
 * Read-only synthesis of the most strategic KPIs derived from existing state.
 * Designed as a 1-screen pilot view.
 */
export default function Strategie() {
  const { t } = useTranslation();
  const {
    company, drivers, vehicles, activeContracts, treasury,
    monthlyRevenues, monthlyExpenses, reputation, notoriety,
    hiredSupport, activeInvestments, activeResolution,
    challengeHistory, attritionHistory, auditHistory, repHistory,
    totalDays, yearlyReports, monthlyHistory, phase,
    credits, activePlacements, tvaCollectedTotal, tvaDeductibleTotal, annualProfit,
  } = useGameStore();

  // === Balance sheet (Patrimoine flotte + Capitaux propres) ===
  const balanceSheet = useMemo(() => computeBalanceSheet({
    treasury, vehicles, credits, activePlacements, totalDays,
    tvaCollectedTotal, tvaDeductibleTotal, annualProfit,
  }), [
    treasury, vehicles, credits, activePlacements, totalDays,
    tvaCollectedTotal, tvaDeductibleTotal, annualProfit,
  ]);

  // Personality compatibility matrix is gated behind hiring Nadia Cheikh
  // (Responsable RH). Same gate as in /hr to keep the UX consistent.
  const hrInsightsUnlocked = !!(hiredSupport || {})['role-nadia-cheikh'];

  // === KPI 1 : Démarchage success rate ===
  const challengeStats = useMemo(() => {
    const h = challengeHistory || [];
    const total = h.length;
    const wins = h.filter(c => c.status === 'success').length;
    return { total, wins, rate: total > 0 ? Math.round((wins / total) * 100) : null };
  }, [challengeHistory]);

  // === KPI 2 : Churn rate (12-month rolling) ===
  const churn = useMemo(() => {
    const h = attritionHistory || [];
    const recent = h.filter(r => (totalDays - r.day) <= 360);
    const totalDrivers = (drivers || []).length + recent.length; // approximate
    const rate = totalDrivers > 0 ? Math.round((recent.length / totalDrivers) * 100) : 0;
    return { count: recent.length, rate };
  }, [attritionHistory, drivers, totalDays]);

  // === KPI 3 : Depot productivity ===
  const productivity = useMemo(() => {
    const depotEnergy = INVESTMENTS.filter(i => i.category === 'Dépôt' || i.category === 'Énergie'); // i18n-keep — data category keys
    const active = depotEnergy.filter(i => activeInvestments?.[i.id])
      .reduce((s, i) => s + Math.abs(i.impactMultiplier || 0), 0);
    const max = depotEnergy.reduce((s, i) => s + Math.abs(i.impactMultiplier || 0), 0);
    return max > 0 ? Math.round((active / max) * 100) : 0;
  }, [activeInvestments]);

  // === KPI 4 : Average gross margin per contract (TMS-gated) ===
  const hasTMS = !!(activeInvestments || {})['inv-tech-01'];
  const hasCommercial = !!(hiredSupport || {})['role-lea-bertrand']
    || !!(hiredSupport || {})['role-marc-dupuis']
    || !!(hiredSupport || {})['role-sylvie-marchand'];
  const marginUnlocked = hasTMS && hasCommercial;
  const avgMargin = useMemo(() => {
    if (!marginUnlocked) return null;
    const list = (activeContracts || []).filter(c => c.vehicleId);
    if (list.length === 0) return null;
    let totalMargin = 0; let count = 0;
    list.forEach(c => {
      const v = vehicles.find(vv => vv.id === c.vehicleId);
      const d = drivers.find(dd => dd.id === c.driverId);
      if (v && d) {
        const r = estimateContractMargin(c, [v], [d]);
        if (r && Number.isFinite(r.margin)) { totalMargin += r.margin; count++; }
      }
    });
    return count > 0 ? Math.round(totalMargin / count) : null;
  }, [activeContracts, vehicles, drivers, marginUnlocked]);

  // === KPI 5 : Fleet utilisation ===
  const fleetUtilisation = useMemo(() => {
    const total = (vehicles || []).length;
    const active = (vehicles || []).filter(v => v.status === 'active').length;
    return { total, active, rate: total > 0 ? Math.round((active / total) * 100) : 0 };
  }, [vehicles]);

  // === KPI 6 : Average driver satisfaction ===
  const avgSat = useMemo(() => {
    const arr = (drivers || []).map(d => d.satisfaction || 0);
    if (arr.length === 0) return null;
    return Math.round(arr.reduce((s, x) => s + x, 0) / arr.length);
  }, [drivers]);

  // === KPI 7 : Compliance score ===
  const compliance = useMemo(() => {
    let s = 50;
    const premium = (vehicles || []).some(v => v.insuranceCoverage === 'premium');
    if (premium) s += 20;
    if (activeInvestments?.['inv-tech-04']) s += 15;
    if (hiredSupport?.['role-christophe-faure']) s += 10;
    if (hiredSupport?.['role-lucas-fontaine']) s += 5;
    if (activeResolution?.type === 'restructuring' && totalDays < (activeResolution.expiresAt || 0)) s -= 25;
    if ((notoriety || 0) > 70) s += 5;
    return Math.max(0, Math.min(100, s));
  }, [vehicles, activeInvestments, hiredSupport, activeResolution, totalDays, notoriety]);

  // === KPI 8 : Cash runway (months) ===
  const cashRunway = useMemo(() => {
    const exp = monthlyExpenses || 1;
    if (exp <= 0) return Infinity;
    return Math.max(0, Math.floor(treasury / exp));
  }, [monthlyExpenses, treasury]);

  // === KPI 9 : Net result of current month ===
  const netMonth = (monthlyRevenues || 0) - (monthlyExpenses || 0);

  // Year-over-year revenue evolution (if 2 closed years exist).
  const yoyDelta = useMemo(() => {
    const yr = yearlyReports || [];
    if (yr.length < 2) return null;
    const last = yr[yr.length - 1];
    const prev = yr[yr.length - 2];
    if (!prev.totalRevenue) return null;
    return Math.round(((last.totalRevenue - prev.totalRevenue) / prev.totalRevenue) * 100);
  }, [yearlyReports]);

  // Audit summary
  const auditStats = useMemo(() => {
    const list = auditHistory || [];
    const cleanCount = list.filter(a => a.outcome === 'clean').length;
    const finedSum = list.reduce((s, a) => s + (a.fine || 0), 0);
    return { total: list.length, clean: cleanCount, finedSum };
  }, [auditHistory]);

  // === KPI 10 : Cost per km (fleet average) ===
  // Cost-per-km = (daily fuel × 30 + monthly maintenance) ÷ (kmPerDay × 30)
  // Aggregated across the player's CURRENT fleet, weighted by kmPerDay so a
  // semi-trailer counts more than a city van.
  const costPerKm = useMemo(() => {
    const list = (vehicles || []).map(v => {
      const def = VEHICLES.find(d => d.id === v.dataId);
      const kmDay = def?.kmPerDay || 100;
      const fuelDay = v.dailyFuel || def?.dailyFuel || 0;
      const maintMonth = v.maintenancePerMonth || def?.maintenancePerMonth || 0;
      const monthlyKm = kmDay * 30;
      const monthlyCost = fuelDay * 30 + maintMonth;
      return monthlyKm > 0 ? { cpk: monthlyCost / monthlyKm, monthlyKm } : null;
    }).filter(Boolean);
    if (list.length === 0) return null;
    const totalKm = list.reduce((s, x) => s + x.monthlyKm, 0);
    const weighted = list.reduce((s, x) => s + x.cpk * x.monthlyKm, 0);
    return totalKm > 0 ? weighted / totalKm : null;
  }, [vehicles]);

  return (
    <div className="space-y-4" data-testid="strategie-page">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-accent/15">
          <LineChartIcon className="w-5 h-5 text-accent" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">{t('strat_title')}</h2>
          <p className="text-xs text-slate-500">{company?.name || 'SCD'} {t('strat_subtitle')}</p>
        </div>
      </div>

      {/* Top row: 4 financial KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          testId="kpi-revenue"
          label={t('strat_kpi_revenue')}
          value={formatMoney(Math.round(monthlyRevenues || 0))}
          icon={TrendingUp}
          color="money"
          hint={yoyDelta !== null ? `YoY ${yoyDelta >= 0 ? '+' : ''}${yoyDelta}%` : null}
        />
        <KpiCard
          testId="kpi-netresult"
          label={t('strat_kpi_net_result')}
          value={`${netMonth >= 0 ? '+' : ''}${formatMoney(Math.round(netMonth))}`}
          icon={netMonth >= 0 ? TrendingUp : TrendingDown}
          color={netMonth >= 0 ? 'money' : 'danger'}
        />
        <KpiCard
          testId="kpi-runway"
          label={t('strat_kpi_runway')}
          value={Number.isFinite(cashRunway) ? `${cashRunway} ${t('months_short')}` : '∞'}
          icon={Briefcase}
          color={cashRunway >= 6 ? 'money' : cashRunway >= 3 ? 'warn' : 'danger'}
          hint={t('strat_treasury_hint', { value: formatMoney(Math.round(treasury || 0)) })}
        />
        <KpiCard
          testId="kpi-margin"
          label={t('strat_kpi_avg_margin')}
          value={avgMargin !== null ? `${avgMargin >= 0 ? '+' : ''}${formatMoney(avgMargin)}` : t('strat_locked_value')}
          icon={Award}
          color={avgMargin === null ? 'slate' : avgMargin >= 0 ? 'money' : 'danger'}
          hint={avgMargin === null ? t('strat_unlock_with_tms') : null}
        />
      </div>

      {/* Middle row: 4 operational KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          testId="kpi-driver-satisfaction"
          label={t('strat_kpi_driver_satisfaction')}
          value={avgSat !== null ? `${avgSat}/100` : '—'}
          icon={Users}
          color={avgSat === null ? 'slate' : avgSat >= 70 ? 'money' : avgSat >= 50 ? 'warn' : 'danger'}
          hint={t('strat_drivers_count_hint', { count: (drivers || []).length })}
        />
        <KpiCard
          testId="kpi-churn"
          label={t('strat_kpi_churn')}
          value={`${churn.rate}%`}
          icon={Users}
          color={churn.rate <= 5 ? 'money' : churn.rate <= 15 ? 'warn' : 'danger'}
          hint={t('strat_resignations_hint', { count: churn.count })}
        />
      </div>

      {/* Bottom row: 4 strategic KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          testId="kpi-cost-per-km"
          label={t('strat_kpi_cost_per_km')}
          value={costPerKm !== null ? `${costPerKm.toFixed(2)} €/km` : '—'}
          icon={Gauge}
          color={costPerKm === null ? 'slate' : costPerKm <= 0.50 ? 'money' : costPerKm <= 0.75 ? 'warn' : 'danger'}
          hint={costPerKm !== null ? t('strat_fuel_maintenance_hint') : t('strat_buy_vehicle_hint')}
        />
        <KpiCard
          testId="kpi-challenge-rate"
          label={t('strat_kpi_challenge_rate')}
          value={challengeStats.rate !== null ? `${challengeStats.rate}%` : '—'}
          icon={Target}
          color={challengeStats.rate === null ? 'slate' : challengeStats.rate >= 50 ? 'money' : 'warn'}
          hint={t('strat_challenge_hint', { wins: challengeStats.wins, total: challengeStats.total })}
        />
        <KpiCard
          testId="kpi-compliance"
          label={t('strat_kpi_compliance')}
          value={`${compliance}/100`}
          icon={ShieldCheck}
          color={compliance >= 70 ? 'money' : compliance >= 40 ? 'warn' : 'danger'}
          hint={compliance < 40 ? t('strat_audit_high_risk') : null}
        />
        <KpiCard
          testId="kpi-audits"
          label={t('strat_kpi_audits')}
          value={auditStats.total > 0 ? `${auditStats.clean}/${auditStats.total} ${t('strat_clean_short')}` : '—'}
          icon={Scale}
          color={auditStats.total === 0 ? 'slate' : auditStats.clean === auditStats.total ? 'money' : 'warn'}
          hint={auditStats.finedSum > 0 ? t('strat_redresses_hint', { value: formatMoney(auditStats.finedSum) }) : null}
        />
      </div>

      {/* Patrimoine row: Capitaux propres + Patrimoine flotte */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="patrimoine-row">
        <KpiCard
          testId="kpi-equity"
          label={t('strat_kpi_equity')}
          value={`${balanceSheet.equity >= 0 ? '+' : ''}${formatMoney(balanceSheet.equity)}`}
          icon={Scale}
          color={balanceSheet.equity < 0 ? 'danger' : balanceSheet.solvencyPct >= 30 ? 'money' : 'warn'}
          hint={t('strat_solvency_hint', { pct: balanceSheet.solvencyPct })}
        />
        <KpiCard
          testId="kpi-debt-ratio"
          label={t('strat_kpi_debt_ratio')}
          value={`${balanceSheet.debtRatioPct} %`}
          icon={Briefcase}
          color={balanceSheet.debtRatioPct <= 50 ? 'money' : balanceSheet.debtRatioPct <= 75 ? 'warn' : 'danger'}
          hint={t('strat_passif_hint', { value: formatMoney(balanceSheet.passif.total) })}
        />
        <KpiCard
          testId="kpi-actif-total"
          label={t('strat_kpi_actif_total')}
          value={formatMoney(balanceSheet.actif.total)}
          icon={TrendingUp}
          color="money"
          hint={t('strat_actif_breakdown_hint', { cash: formatMoney(balanceSheet.actif.cash), fleet: formatMoney(balanceSheet.actif.fleetGross) })}
        />
      </div>

      {/* 17/06/2026 — Volet pédagogique de décomposition fiscale du bilan
          (provisions TVA/IS/Réserve fiscale à l'actif compensent les
          dettes fiscales au passif). Cible le retour user qui voyait des
          capitaux propres sous-estimés. */}
      <BilanFiscalBreakdown balanceSheet={balanceSheet} />

      {/* Reputation / Notoriety temporal chart */}
      {(repHistory && repHistory.length >= 2) && (
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-4" data-testid="rep-noto-chart-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-semibold text-slate-200">{t('strat_rep_noto_title')}</h3>
            </div>
            <p className="text-[10px] text-slate-500 font-mono">{t('strat_rep_noto_measures', { count: repHistory.length })}</p>
          </div>
          <div className="h-48" data-testid="rep-noto-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={repHistory} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                <XAxis dataKey="day" stroke="#64748b" fontSize={10} tickFormatter={(d) => `J${d}`} />
                <YAxis stroke="#64748b" fontSize={10} domain={Y_DOMAIN} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  labelFormatter={(d) => t('strat_day_label', { day: d })}
                />
                <Legend wrapperStyle={LEGEND_STYLE} />
                <Line type="monotone" dataKey="reputation" stroke="#fbbf24" strokeWidth={2} dot={false} name={t('reputation_short')} />
                <Line type="monotone" dataKey="notoriety"  stroke="#a855f7" strokeWidth={2} dot={false} name={t('notoriety_short')} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Strategic flags */}
      {(activeResolution || compliance < 40 || avgSat !== null && avgSat < 40 || cashRunway < 3) && (
        <div className="bg-ink-800 rounded-lg border border-warn/30 p-4">
          <h3 className="text-sm font-semibold text-slate-200 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-warn" /> {t('strat_alerts_title')}
          </h3>
          <ul className="space-y-1 text-xs">
            {cashRunway < 3 && (
              <li className="flex items-center gap-2 text-danger" data-testid="alert-runway">
                <span className="w-1.5 h-1.5 rounded-full bg-danger" /> {t('strat_alert_runway')}
              </li>
            )}
            {avgSat !== null && avgSat < 40 && (
              <li className="flex items-center gap-2 text-danger" data-testid="alert-satisfaction">
                <span className="w-1.5 h-1.5 rounded-full bg-danger" /> {t('strat_alert_satisfaction')}
              </li>
            )}
            {compliance < 40 && (
              <li className="flex items-center gap-2 text-warn" data-testid="alert-compliance">
                <span className="w-1.5 h-1.5 rounded-full bg-warn" /> {t('strat_alert_compliance')}
              </li>
            )}
            {activeResolution && totalDays < (activeResolution.expiresAt || 0) && (
              <li className="flex items-center gap-2 text-accent" data-testid="alert-resolution">
                <span className="w-1.5 h-1.5 rounded-full bg-accent" /> {t('strat_alert_resolution_prefix')} <strong>{activeResolution.type}</strong> {t('strat_alert_resolution_until', { day: activeResolution.expiresAt })}
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Team compatibility matrix and RSE/HSE dashboard moved to /hr
          on 02/06/2026 (player feedback). Strategie keeps the support
          benefits panel + KPIs. */}

      {/* 08/02/2026 — Q2 user prod : « Avantages des fonctions support »
          retiré (info dupliquée avec /fonctions-support → sous-onglet
          « Impact métier »). Strategie reste centré sur les KPI macro,
          la perf commerciale et le ledger contrats. */}

      {/* 07/02/2026 — Performance commerciale déplacée depuis Fonctions
          Support (retour user prod : sa place est plus naturelle dans
          l'analyse stratégique du dashboard CEO). */}
      <CommercialPerformancePanel />

      {/* 08/02/2026 — Historique des contrats + export CSV pour calibrage
          de l'économie (demande utilisateur post-partie Rouleboule). */}
      <ContractLedgerPanel />
    </div>
  );
}

const KpiCard = ({ testId, label, value, icon: Icon, color = 'slate', hint }) => {
  const colorMap = {
    money:  'text-money border-money/30 bg-money/5',
    danger: 'text-danger border-danger/30 bg-danger/5',
    warn:   'text-warn border-warn/30 bg-warn/5',
    slate:  'text-slate-300 border-ink-600 bg-ink-700/30',
  };
  return (
    <div
      className={`rounded-xl border p-3 ${colorMap[color] || colorMap.slate}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <p className="text-[10px] uppercase tracking-wider opacity-80 font-mono">{label}</p>
      </div>
      <p className="text-lg font-black font-mono">{value}</p>
      {hint && <p className="text-[10px] text-slate-500 mt-0.5">{hint}</p>}
    </div>
  );
};
